import { guestSessionHeaderName, possibleTypes } from '@aline/common';
import { routes } from '@aline/routes';
import { ApolloClient, ApolloLink, InMemoryCache } from '@apollo/client';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import { SetContextLink } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import UploadHttpLink from 'apollo-upload-client/UploadHttpLink.mjs';
import { createClient } from 'graphql-ws';
import { getGuestSessionToken, getWebsocketConnectionParams } from 'src/auth';
import { getClientPublicOrigin, isDev, subscriptionsEndpoint } from 'src/config';
import { typePolicies } from 'src/gql/apollo-type-policies';

import { errorLink } from './apollo-error-link';

// In development, load error messages for better debugging
// These are omitted from production bundle by default to reduce size
if (isDev) {
  loadDevMessages();
  loadErrorMessages();
}

/**
 * Tab-scoped guest sessions live in sessionStorage (not cookies), so HTTP
 * GraphQL must attach them explicitly. Prefer this over the WorkOS cookie.
 */
const guestSessionLink = new SetContextLink(({ headers }) => {
  const guestToken = getGuestSessionToken();
  if (!guestToken) return { headers };

  return {
    headers: {
      ...headers,
      [guestSessionHeaderName]: guestToken,
    },
  };
});
const uploadLink = new UploadHttpLink({
  // See https://www.apollographql.com/docs/react/networking/authentication/#cookie.
  credentials: 'same-origin',
  headers: {
    /**
     * Required because of `csrfPrevention: true` on Apollo Server.
     * @url https://www.apollographql.com/docs/apollo-server/v3/data/file-uploads/
     */
    'Apollo-Require-Preflight': 'true',
  },
  uri: `${getClientPublicOrigin()}${routes.ApiGraphQL}`,
});

/**
 * Custom link to inject fresh auth cookies into subscription extensions.
 * This ensures each subscription uses the current cookie from the browser,
 * not the stale cookie from the initial WebSocket handshake.
 */
const wsAuthLink = new ApolloLink((operation, forward) => {
  const auth = getWebsocketConnectionParams();

  // Add auth to the operation's extensions (sent in each GraphQL payload).
  operation.extensions = {
    ...operation.extensions,
    auth,
  };

  return forward(operation);
});

const wsLink = new GraphQLWsLink(
  createClient({
    lazy: true,
    retryAttempts: 10,
    shouldRetry: () => {
      // Retry as long as we have valid auth cookies.
      const auth = getWebsocketConnectionParams();
      return Boolean(auth);
    },
    url: subscriptionsEndpoint,
  })
);

// Use websocket link only for subscriptions, everything else goes through http/the upload link.
const splitLink = ApolloLink.split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  ApolloLink.from([wsAuthLink, wsLink]),
  uploadLink
);

export const apolloClient = new ApolloClient({
  cache: new InMemoryCache({
    possibleTypes,
    typePolicies,
  }),

  defaultOptions: {
    // Retains all error information.
    mutate: { errorPolicy: 'all' },
    query: { errorPolicy: 'all' },
  },
  link: ApolloLink.from([guestSessionLink, errorLink, splitLink]),
});
